import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { API_ENDPOINTS } from "../../../helpers/constants";
import { validateApiParamValue } from "../../../helpers/utils";
import apiInstance from "../../instances/apiInstance";
import {
  MdmActivityViewParams,
  MdmAddActivityParams,
  SearchMdmActivityParams,
  TopicListParams,
} from "./models";

export const useMdmSearchActivityMutation = () => {
  return useMutation({
    mutationFn: (params: SearchMdmActivityParams) => {
      return apiInstance.post(API_ENDPOINTS.GET_MDM_SEARCH_ACTIVITY, params);
    },
    retry: false,
  });
};

export const useGetExistingTopics = () => {
  const [isCall, setIsCall] = useState<boolean>(false);
  const query = useQuery({
    queryKey: ["useGetExistingTopics"],
    queryFn: async () => {
      if (!isCall) {
        return [];
      }
      const res = await apiInstance.get(API_ENDPOINTS.GET_MDM_EXISTING_TOPICS);
      return res.data;
    },
    retry: false,
    enabled: !!isCall,
  });
  const refetchWithParams = () => {
    setIsCall(true);
  };
  return { ...query, refetchWithParams };
};

export const useMdmAddActivityMutation = () => {
  return useMutation({
    mutationFn: (params: MdmAddActivityParams) => {
      return apiInstance.post(API_ENDPOINTS.MDM_ADD_ACTIVITY, params);
    },
    retry: false,
  });
};

export const useMdmUpdateActivityMutation = () => {
  return useMutation({
    mutationFn: (params: MdmAddActivityParams) => {
      return apiInstance.put(
        API_ENDPOINTS.MDM_UPDATE_ACTIVITY(params.id),
        params,
      );
    },
    retry: false,
  });
};

export const useMdmGetViewActivityDetailById = () => {
  const [params, setParams] = useState<MdmActivityViewParams>();
  const query = useQuery({
    queryKey: ["getViewActivityDetailById", params],
    queryFn: async () => {
      if (!params) {
        return [];
      }
      const { id = "" } = params;
      const res = await apiInstance.get(API_ENDPOINTS.MDM_VIEW_ACTIVITY(id));
      return res.data;
    },
    enabled: !!params, // Only run when params are set
    retry: false,
  });

  const refetchWithParams = (newParams: MdmActivityViewParams) => {
    setParams((prevParams) => ({
      ...newParams,
      isRefetch: !prevParams?.isRefetch, // Flip the boolean value
    }));
  };

  return { ...query, refetchWithParams };
};

export const useGetMdmTopicDetailsById = (topicId: string) => {
  return useQuery({
    queryKey: ["useGetMdmTopicDetailsById", topicId],
    queryFn: async () => {
      const res = await apiInstance.get(
        `${API_ENDPOINTS.MDM_VIEW_TOPIC}/${validateApiParamValue(topicId)}`,
      );
      return res.data;
    },
    retry: false,
  });
};

export const useMdmUpdateTopicDetails = () => {
  return useMutation({
    mutationFn: (params: TopicListParams) => {
      return apiInstance.put(
        `${API_ENDPOINTS.MDM_UPDATE_TOPIC}/${params.id}`,
        params,
      );
    },
    retry: false,
  });
};
